import { ArticleDataContext } from 'articleEditor/contexts/articleDataProvider';
import { TranslationDataContext } from 'articleEditor/contexts/translationProvider';
import { useCollectionId } from 'articleEditor/hooks/useCollectionId';
import { useTranslationUpdate } from 'articleEditor/hooks/useTranslationUpdate';
import {
  getEmptyValue,
  isValidArticleStructure,
  transformArticleStructure,
} from 'lib-knowledge-document-structure';
import { useTriggerEvent } from 'lib-knowledge-utils/analytics';
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useSnapshot } from 'valtio';

import { online } from './savingContext.utils';
import { ArticleSavingProxy, createArticleSavingProxy } from './savingStore';

export const ArticleSavingContext = createContext<ArticleSavingProxy>(
  {} as ArticleSavingProxy,
);

type ArticleSavingProviderProps = {
  children: ReactNode;
};

export const ArticleSavingProvider = ({
  children,
}: ArticleSavingProviderProps): JSX.Element => {
  const collectionId = useCollectionId();
  const { id: articleId, isSyncedWithElevio } = useContext(ArticleDataContext);
  const articleTranslation = useContext(TranslationDataContext);
  const currentLocale = articleTranslation?.draft.languageId;
  const [saveFn] = useTranslationUpdate(articleId, currentLocale!);

  // resetKey allows us to regenerate the store when the translation changes and reset the editor.
  const [resetKey, setResetKey] = useState(0);
  const resetEditor = useCallback(
    () => setResetKey((k) => k + 1),
    [setResetKey],
  );
  const triggerEvent = useTriggerEvent();

  const store = useMemo(() => {
    const title = articleTranslation.draft?.title || '';
    const body = getTranslationBody(articleTranslation);

    return createArticleSavingProxy({
      initialTitle: title,
      initialBody: body,
      initialTittleUpdatedAt:
        articleTranslation.draft.titleUpdatedAt ?? undefined,
      initialTitleTranslatedAt:
        articleTranslation.draft.titleTranslatedAt ?? undefined,
      saveFn,
      editorReadonly: isSyncedWithElevio,
      resetEditor,
      triggerEvent,
    });

    // Don't want to re-create the store if the translation updates as it updates on every save.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [articleId, collectionId, saveFn, currentLocale, resetKey, resetEditor]);

  // If we are leaving the page, then flush any unwritten saves.
  useEffect(() => {
    // addEventListener('beforeunload', store.actions.debouncedSave.flush);
    return () => {
      store.actions.debouncedSave.flush();
    };
  }, [store]);

  // Make sure we write any changes if we were offline.
  const { isOnline } = useSnapshot(online);
  useEffect(() => {
    if (isOnline) store.actions.debouncedSave.flush();
  }, [isOnline, store.actions]);

  return (
    <ArticleSavingContext.Provider value={store} key={resetKey}>
      {children}
    </ArticleSavingContext.Provider>
  );
};

function getTranslationBody(articleTranslation: TranslationDataContext) {
  const content = articleTranslation?.draft?.content;
  // Slate needs a base structure otherwise it crashes.
  if (!isValidArticleStructure(content)) return getEmptyValue();
  if (content?.version === '1.0.0') {
    return transformArticleStructure(content).children;
  }
  return content.children;
}
